import React from "react";
import { ArrowRight, Loader2 } from "lucide-react";

import { NOJO_ROADMAP_ITEMS } from "@/data/nojoCorporateUpdates";

/**
 * MilestonesInProgress — compact "what we're chasing next" widget.
 *
 * Placed on the About page directly under the completed-milestones
 * timeline so a reader who scrolled the past 13 years of milestones
 * gets a natural transition into what's actively in flight.
 *
 * Data source: `NOJO_ROADMAP_ITEMS` (hand-maintained bilingual data
 * file). Only entries with `status: "in_progress"` render — completed
 * items belong in the timeline above, planned items belong on the
 * dedicated `/roadmap` page. If no in-progress items exist the widget
 * quietly renders `null` and leaves the page unchanged.
 *
 * Owner P2 feature (2026-06-21) — shipped alongside the June 2026
 * 18,000-customer milestone rollout.
 *
 * Brand palette (STRICT): purple #663399 for the "in progress" pill,
 * green #78D33C for the pulse dot, white cards on the About page's
 * dark-navy background. Absolutely no black buttons or backgrounds.
 */
export default function MilestonesInProgress({ lang = "en" }) {
  const isJa = lang === "ja";
  const inProgress = NOJO_ROADMAP_ITEMS.filter(
    (it) => it.status === "in_progress"
  ).slice(0, 3);
  if (inProgress.length === 0) return null;

  return (
    <div
      data-testid="about-milestones-in-progress"
      className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 md:p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: "#78D33C" }}
        />
        <h4 className="text-[11px] font-mono-display uppercase tracking-[0.18em] font-bold text-white/70">
          {isJa ? "現在進行中の節目" : "Milestones we're chasing next"}
        </h4>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {inProgress.map((item) => {
          const title =
            (item.title && (isJa ? item.title.ja : item.title.en)) || "";
          const eta =
            item.eta && (isJa ? item.eta.ja : item.eta.en);
          return (
            <li
              key={item.id}
              data-testid={`about-milestone-inprogress-${item.id}`}
              className="rounded-xl bg-[#0F1D2D]/40 ring-1 ring-white/5 p-4 flex flex-col gap-2"
            >
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-mono-display uppercase tracking-[0.16em] font-bold"
                  style={{ backgroundColor: "#663399", color: "#FFFFFF" }}
                >
                  <Loader2
                    className="h-2.5 w-2.5 animate-spin"
                    strokeWidth={2.5}
                  />
                  {isJa ? "進行中" : "In progress"}
                </span>
                {eta && (
                  <span className="text-[10px] font-mono-display uppercase tracking-[0.14em] text-white/50">
                    {eta}
                  </span>
                )}
              </div>
              <p className="text-[12.5px] leading-[1.5] text-white/90 font-semibold">
                {title}
              </p>
            </li>
          );
        })}
      </ul>
      <a
        href={`/${lang}/roadmap`}
        data-testid="about-milestones-in-progress-link"
        className="mt-4 inline-flex items-center gap-1 text-[11px] font-mono-display uppercase tracking-[0.16em] font-bold text-white/70 hover:text-white transition-colors"
      >
        {isJa ? "全社ロードマップを見る" : "See the full roadmap"}
        <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
      </a>
    </div>
  );
}
